import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  ActionButton,
  ActionGroup,
  AppDialog,
  EmptyState,
  LoadingState,
  PageToolbar,
  StatusBadge,
} from '../components/app';
import { EmployeeForm } from '../components/employees';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  User,
  Envelope,
  Phone,
  Briefcase,
  CurrencyDollar,
  Star,
  Eye,
  Archive,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SKILLS = ['frontend', 'backend', 'mobile', 'qa', 'design', 'devops', 'pm', 'soporte'];
const TECHNOLOGIES = ['React', 'Vue', 'Angular', 'Node.js', 'Python', 'Java', 'Go', 'PHP', 'Swift', 'Kotlin', 'Flutter', 'AWS', 'Docker', 'Kubernetes'];

export function EmployeesPage() {
  const { t, isSpanish } = useLanguage();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    document_id: '',
    position: '',
    department: '',
    team: 'administrativo',  // NUEVO
    hire_date: '',
    modality: 'tiempo_completo',
    payment_type: 'salario_fijo',
    level: 'junior',
    skills: [],
    technologies: [],
    weekly_capacity: 40,
    internal_cost: 0,
    hourly_rate: 0,
    bank_info: '',
    status: 'activo',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchEmployees = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await getEmployees(params);
      setEmployees(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee._id, formData);
        toast.success(t('successSaved'));
      } else {
        await createEmployee(formData);
        toast.success(t('successSaved'));
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      address: employee.address || '',
      document_id: employee.document_id || '',
      position: employee.position || '',
      department: employee.department || '',
      team: employee.team || 'administrativo',
      hire_date: employee.hire_date || '',
      modality: employee.modality || 'tiempo_completo',
      payment_type: employee.payment_type || 'salario_fijo',
      level: employee.level || 'junior',
      skills: employee.skills || [],
      technologies: employee.technologies || [],
      weekly_capacity: employee.weekly_capacity || 40,
      internal_cost: employee.internal_cost || 0,
      hourly_rate: employee.hourly_rate || 0,
      bank_info: employee.bank_info || '',
      status: employee.status || 'activo',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await deleteEmployee(id);
        toast.success(t('successDeleted'));
        fetchEmployees();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      document_id: '',
      position: '',
      department: '',
      team: 'administrativo',
      hire_date: '',
      modality: 'tiempo_completo',
      payment_type: 'salario_fijo',
      level: 'junior',
      skills: [],
      technologies: [],
      weekly_capacity: 40,
      internal_cost: 0,
      hourly_rate: 0,
      bank_info: '',
      status: 'activo',
    });
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      emp.name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.position?.toLowerCase().includes(searchLower)
    );
    const matchesTeam = teamFilter === 'all' || emp.team === teamFilter;
    return matchesSearch && matchesTeam;
  });

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const toggleTech = (tech) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.includes(tech)
        ? prev.technologies.filter(t => t !== tech)
        : [...prev.technologies, tech]
    }));
  };

  return (
    <Layout title={t('employees')}>
      <div className="space-y-6" data-testid="employees-page">
        {/* Header */}
        <PageToolbar
          filters={
            <>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="employee-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="activo">{t('active')}</SelectItem>
                <SelectItem value="inactivo">{t('inactive')}</SelectItem>
                <SelectItem value="suspendido">Suspendido</SelectItem>
              </SelectContent>
            </Select>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-44 valhalla-input" data-testid="team-filter">
                <SelectValue placeholder={isSpanish ? 'Equipo' : 'Team'} />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{isSpanish ? 'Todos los equipos' : 'All teams'}</SelectItem>
                <SelectItem value="desarrollo">Desarrollo</SelectItem>
                <SelectItem value="diseno">Diseno</SelectItem>
                <SelectItem value="qa">QA</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="ventas">Ventas</SelectItem>
                <SelectItem value="administrativo">Administrativo</SelectItem>
              </SelectContent>
            </Select>
            </>
          }
          actions={
            <AppDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) resetForm();
              }}
              size="xl"
              title={editingEmployee ? t('edit') : t('newEmployee')}
              trigger={
                <ActionButton
                  icon={Plus}
                  label={t('newEmployee')}
                  tone="primary"
                  className="font-semibold"
                  data-testid="new-employee-btn"
                />
              }
            >
              <EmployeeForm
                formData={formData}
                setFormData={setFormData}
                skills={SKILLS}
                technologies={TECHNOLOGIES}
                toggleSkill={toggleSkill}
                toggleTech={toggleTech}
                t={t}
                isSpanish={isSpanish}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
              />
            </AppDialog>
          }
        />

        {/* Employees Grid */}
        {loading ? (
          <LoadingState label={t('loading')} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEmployees.map((employee) => (
            <div key={employee._id} className="valhalla-card p-5" data-testid={`employee-card-${employee._id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-bold text-lg">
                    {employee.name?.charAt(0)}{employee.last_name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#F3F4F6]">
                      {employee.name} {employee.last_name}
                    </h3>
                    <p className="text-sm text-[#9CA3AF]">{employee.position}</p>
                  </div>
                </div>
                <StatusBadge value={employee.status} />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Envelope className="w-4 h-4" />
                  <span className="truncate">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <Phone className="w-4 h-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Briefcase className="w-4 h-4" />
                  <span>{employee.department || 'No department'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#9CA3AF]" />
                  <StatusBadge value={employee.level} />
                  {employee.team && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      {employee.team}
                    </Badge>
                  )}
                </div>
              </div>

              {employee.skills?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {employee.skills.slice(0, 3).map(skill => (
                    <Badge key={skill} variant="outline" className="text-xs border-[#242E42] text-[#9CA3AF]">
                      {skill}
                    </Badge>
                  ))}
                  {employee.skills.length > 3 && (
                    <Badge variant="outline" className="text-xs border-[#242E42] text-[#9CA3AF]">
                      +{employee.skills.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-[#242E42] flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-[#9CA3AF]">${employee.hourly_rate || 0}/hr</span>
                </div>
                <ActionGroup className="gap-1">
                  <ActionButton
                    icon={PencilSimple}
                    tone="edit"
                    iconOnly
                    onClick={() => handleEdit(employee)}
                    data-testid={`edit-employee-${employee._id}`}
                  />
                  <ActionButton
                    icon={Eye}
                    tone="warning"
                    iconOnly
                    onClick={() => navigate(`/employees/${employee._id}`)}
                    title={isSpanish ? 'Ver Dashboard' : 'View Dashboard'}
                  />
                  <ActionButton
                    icon={employee.status === 'archivado' ? Archive : Trash}
                    tone="delete"
                    iconOnly
                    onClick={() => handleDelete(employee._id)}
                    data-testid={`delete-employee-${employee._id}`}
                  />
                </ActionGroup>
              </div>
            </div>
          ))}
          </div>
        )}

        {filteredEmployees.length === 0 && !loading && (
          <EmptyState icon={User} description={t('noData')} />
        )}
      </div>
    </Layout>
  );
}

export default EmployeesPage;
